<template>
  <div>
    <div class="chat-assistance-container" :class="{'embedded-view': isEmbedded === true, 'rtl': isRTL === true}">
      <div v-if="isLoadingAgentInfo === false && isEmbedded === false" class="chat-header">
        <div class="flex items-center space-x-4"> 

          <button @click="startNewChat" class="btn btn-sm btn-outline">
            <Icon icon="tabler:plus" class="mr-2" />
            {{ t('chat.newChatButton', 'New Chat') }}
          </button>
        </div>
        <div class="agent-info">
          <span class="agent-name">{{ agentName }}</span>
          <span class="agent-status">{{ t('status.active', 'Active') }}</span>
        </div>
      </div>
      <div v-if="isLoadingAgentInfo === false" class="chat-content">
        <div class="chat-messages" ref="chatMessages">
          <template v-for="(message, index) in messages" :key="index">
            <div 
              :class="['message', message.type === 'user' ? 'user-message' : 'agent-message', message.isLoading === true ? 'typing' : '']" 
              :message-id="message.id" 
              :style="{ direction: message.direction, textAlign: message.direction === 'rtl' ? 'right' : 'left' }">
              <div class="message-wrapper">
                <div class="message-content" :style="{ direction: message.direction, textAlign: message.direction === 'rtl' ? 'right' : 'left' }">
                  <template v-if="message.isLoading === true">
                    <svg width="60" height="23" viewBox="0 0 120 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="8"><animateTransform attributeName="transform" type="translate" values="0,0;0,-15;0,0;0,0" keyTimes="0;0.3;0.6;1" calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1" dur="1.2s" repeatCount="indefinite" begin="0s"/></circle><circle cx="60" cy="20" r="8"><animateTransform attributeName="transform" type="translate" values="0,0;0,-15;0,0;0,0" keyTimes="0;0.3;0.6;1" calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1" dur="1.2s" repeatCount="indefinite" begin="0.2s"/></circle><circle cx="100" cy="20" r="8"><animateTransform attributeName="transform" type="translate" values="0,0;0,-15;0,0;0,0" keyTimes="0;0.3;0.6;1" calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1" dur="1.2s" repeatCount="indefinite" begin="0.4s"/></circle></svg>
                  </template>
                  <template v-else>
                    <div v-html="sanitizeHtml(message.text)"></div>
                    <div v-if="message.type === 'user' && message.attachedFiles !== undefined && message.attachedFiles !== null && message.attachedFiles.length > 0" class="attached-files-display">
                      <div v-for="(file, fIndex) in message.attachedFiles" :key="fIndex" class="attached-file-item">
                        <template v-if="file.dataUrl !== null && file.dataUrl !== undefined">
                          <img :src="file.dataUrl" :alt="file.name" class="attached-file-preview-image">
                        </template>
                        <template v-else>
                          <Icon :icon="getFileIcon(isDefinedAndNotEmpty(file.type) === true ? file.type : 'unknown')" class="attached-file-icon" />
                        </template>
                        <span class="attached-file-name" :title="file.name">{{ file.name }}</span>
                      </div>
                    </div>
                    <template v-if="message.type === 'agent' && message.sources !== undefined && message.sources.length > 0">
                      
                      <div class="sources-container">
                        <div class="text-xs font-semibold opacity-70 mb-1 w-full">{{ t('chat.sourcesLabel', 'Sources:') }}</div>
                        <div class="text-xs flex flex-wrap space-x-2">
                          <div v-for="(src, si) in message.sources" :key="si" class="inline-block">
                            <template v-if="src.type === 'website'">
                              <button @click="confirmExternalLink(src)" class="source-chip hover:shadow-md">
                                <Icon icon="tabler:world-www" width="14" class="mr-1" />
                                <span class="truncate max-w-[120px]" :title="decodeURIComponent(src.url)">{{ decodeURIComponent(src.url).replace(/^https?:\/\//,'').split('/')[0] }}</span>
                              </button>
                            </template>
                            <template v-else>
                              <button @click="openSourceModal(src)" class="source-chip hover:shadow-md">
                                <Icon :icon="getFileIconByType(src.name?.split('.').pop())" width="14" class="mr-1" />
                                <span class="truncate max-w-[120px]" :title="src.name">{{ src.name }}</span>
                                <span v-if="src.page !== undefined && src.page !== null" class="ml-1 text-xs opacity-60">p{{ src.page }}</span>
                              </button>
                            </template>
                          </div>
                        </div>
                      </div>
                    </template>
                  </template>
                </div>
                <div v-if="message.type === 'agent' && message.isLoading === false && message.messageId !== undefined" 
                      class="feedback-buttons" 
                      :class="{ 'feedback-provided': feedbackStatus[message.messageId] !== undefined }">
                      <div>

                      
                  <div style="height: 1.5rem;width: 100%;"></div>
                  <div class="flex items-center space-x-2">
<button @click="submitFeedback(message.messageId, 'like')" 
                          title="Good response" 
                          :disabled="pendingFeedback[message.messageId] === true">
                    <Icon :icon="feedbackStatus[message.messageId] === 'like' ? 'tabler:thumb-up' : 'tabler:thumb-up'" width="20" />
                  </button>
                  <button @click="submitFeedback(message.messageId, 'dislike')" 
                          title="Bad response"
                          :disabled="pendingFeedback[message.messageId] === true">
                    <Icon :icon="feedbackStatus[message.messageId] === 'dislike' ? 'tabler:thumb-down' : 'tabler:thumb-down'" width="20" />
                  </button>
                  <button @click="copyToClipboard(message.text)" 
                          title="Copy text">
                    <Icon icon="tabler:copy" width="20" />
                  </button>
                  <div class="dropdown dropdown-top dropdown-end ml-1">
                    <button tabindex="0" role="button" class="btn btn-xs btn-ghost p-1 normal-case" title="Export response as...">
                      <Icon icon="tabler:download" width="20" /> Export
                    </button>
                  
                  
                    
                    <ul tabindex="0" class="dropdown-content z-[1] menu p-1 shadow bg-base-100 rounded-box w-28 text-xs">
                      <li><a @click="downloadTxt(message)">TXT</a></li>
                      <li><a @click="downloadDocx(message)">DOCX</a></li>
                      <li><a @click="downloadPdf(message)">PDF</a></li>
                    </ul>
                  </div>
                </div>
                </div>
                </div>
            </div>
          </div>
          <div v-if="message.type === 'agent' && message.isLoading === false && message.suggestedResponses !== undefined && message.suggestedResponses !== null && message.suggestedResponses.length > 0" class="suggested-responses">
            <button
              v-for="(suggestion, sIndex) in message.suggestedResponses"
              :key="sIndex"
              @click="handleSuggestionClick(suggestion, message.messageId)"
              class="suggestion-button btn btn-sm btn-outline"
            >
              {{ suggestion }}
            </button>
          </div>
        </template>
      </div>
      <div 
        class="chat-input"
        :class="{ 'is-dragging-over': isDragging === true }" 
        @dragenter.prevent.stop="handleDragEnter"
        @dragover.prevent.stop="handleDragOver"
        @dragleave.prevent.stop="handleDragLeave"
        @drop.prevent.stop="handleDrop"
      >
        <div class="staged-files-container" v-if="stagedFiles.length > 0">
          <div v-for="(fileItem, index) in stagedFiles" :key="index" class="file-card">
            <template v-if="fileItem.previewUrl !== null">
              <img :src="fileItem.previewUrl" class="file-preview-image" :alt="fileItem.file.name" />
            </template>
            <template v-else>
              <Icon :icon="getFileIcon(isDefinedAndNotEmpty(fileItem.file?.name?.split('.').pop()?.toLowerCase()) === true ? fileItem.file?.name?.split('.').pop()?.toLowerCase() : 'unknown')" class="file-icon" />
            </template>
            <span class="file-name" :title="fileItem.file.name">{{ fileItem.file.name }}</span>
            <button @click="removeStagedFile(index)" class="remove-file-button">
              <Icon icon="tabler:x" width="14" />
            </button>
          </div>
        </div>
        <div class="input-row">
          <div class="file-upload">
            <input type="file" 
              ref="fileInput" 
              @change="handleFileUpload" 
              multiple 
              class="hidden"
              accept=".pdf,.doc,.docx,.txt,.csv,.json,.md,.png,.jpg,.jpeg,.gif,.bmp,.webp"
            />
            <button @click="triggerFileInput" class="upload-button" title="Attach files">
              <Icon icon="tabler:upload" width="24" />
            </button>
          </div>
          <textarea v-model="userMessage" 
            @keyup.enter.prevent="sendMessage" 
            @input="checkDirection($event.target.value)" 
            :style="{ direction: inputDirection, textAlign: inputDirection === 'rtl' ? 'right' : 'left' }" 
            :placeholder="t('chat.messagePlaceholder')"
            ref="messageInput"
          ></textarea>
          <button @click.prevent="sendMessage" class="send-button" title="Send message">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="#fbfcff" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m5 12l-.604-5.437C4.223 5.007 5.825 3.864 7.24 4.535l11.944 5.658c1.525.722 1.525 2.892 0 3.614L7.24 19.466c-1.415.67-3.017-.472-2.844-2.028zm0 0h7"/></svg>
          </button>
        </div>
      </div>
    </div>
    <div v-else class="flex-grow flex items-center justify-center">
      <span class="loading loading-dots loading-lg text-primary"></span>
    </div>
  </div>

  <dialog class="modal" :class="{ 'modal-open': showAccessDeniedModal === true }">
    <div class="modal-box flex flex-col items-center text-center">
      <h3 class="font-bold text-lg text-error flex items-center space-x-2 mb-4">
        <Icon icon="tabler:circle-x" width="48" /> 
        Access Denied!
      </h3>
      <p class="py-4">You do not have permission to access this AI Agent ({{ agentName }}) because you are not assigned to any of its required divisions.</p>
      <div class="modal-action mt-4">
        <button class="btn" @click="router.push('/')">OK</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="router.push('/')">close</button>
    </form>
  </dialog>

  <button 
    id="knowledge-base-fab"
    v-if="canModifyAgent === true && isLoadingAgentInfo === false && isEmbedded === false" 
    @click="openKbModal"
    class="btn bg-base-200 w-16 h-16 btn-circle btn-md fixed bottom-6 right-6 shadow-lg z-20"
    style="bottom: 14%;">
    <Icon icon="tabler:database" width="28" />
  </button>

  <dialog class="modal" :class="{ 'modal-open': isKbModalOpen === true }">
    <div class="modal-box w-11/12 max-w-3xl relative"> 
      <button 
          class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2 z-10" 
          @click="isKbModalOpen = false" 
          :disabled="isUploadingKb === true">
        <Icon icon="tabler:x" width="20" />
      </button>
      <h3 class="font-bold text-lg mb-4">Agent Knowledge Base</h3>


      <div v-if="kbUploadError !== null" role="alert" class="alert alert-error mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        <span>Error: {{ kbUploadError }}</span>
        <button class="btn btn-sm btn-ghost" @click="kbUploadError = null">OK</button>
      </div>

  
      <div v-if="isUploadingKb === true || isLoadingKb === true" 
           class="absolute inset-0 bg-base-100 bg-opacity-75 flex flex-col items-center justify-center z-20 rounded-box p-4">
           <span class="loading loading-dots loading-lg text-primary mb-4"></span> 
           <span class="text-lg font-semibold text-center">
             Uploading...
           </span>
           <span class="text-sm text-gray-500 mt-1">Please wait.</span>
      </div>

   
      <div class="mb-6" :class="{ 'opacity-50 pointer-events-none': isUploadingKb === true }">
        <div role="tablist" class="tabs tabs-lifted">
          
          <input type="radio" name="kb_upload_tabs" role="tab" class="tab" aria-label="Upload Files" checked />
          <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-4 border">
            
            <label for="kb-file-upload" class="btn btn-block mb-2" :class="{ 'btn-disabled': isUploadingKb === true }">
              <Icon icon="tabler:upload" width="20" class="mr-2" />
              Select Files to Add
            </label>
            <input 
                id="kb-file-upload"
                type="file" 
                @change="handleKbFileUpload" 
                multiple 
                class="hidden"  
                accept=".pdf,.doc,.docx,.txt,.csv,.json,.md,.png,.jpg,.jpeg,.gif,.bmp,.webp"
                :disabled="isUploadingKb === true" 
            />
          
            <div v-if="kbStagedFiles.length > 0" class="mt-3 space-y-2">
              <h4 class="text-sm font-medium">Files ready to upload:</h4>
              <div v-for="(item, index) in kbStagedFiles" :key="index" class="flex items-center justify-between bg-base-200 p-2 rounded text-xs">
                <span class="truncate" :title="item.file.name">{{ item.file.name }}</span>
                <button class="btn btn-xs btn-circle btn-error btn-ghost" @click="removeKbStagedFile(index)" :disabled="isUploadingKb === true">
                  <Icon icon="tabler:x" width="14" style="color: #ef4444;" />
                </button>
              </div>
              <button 
                class="btn btn-sm w-full mt-2" 
                @click="uploadKnowledgeFiles" 
                :disabled="isUploadingKb === true || kbStagedFiles.length === 0">
                  <span v-if="isUploadingKb === true" class="loading loading-spinner loading-xs"></span>
                  {{ isUploadingKb ? 'Uploading...' : 'Upload Staged Files' }}
              </button>
            </div>
            <p v-else class="text-xs text-center text-gray-500 mt-2">Drag & drop or select files to add.</p> 
            
          </div>

          <input type="radio" name="kb_upload_tabs" role="tab" class="tab" aria-label="Add Text" />
          <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-4 border">
            
            <h4 class="text-sm font-medium mb-2">Add Text Snippet:</h4>
            <input 
              type="text"
              v-model="kbTextTitle"
              placeholder="Enter title for text snippet..."
              class="input input-bordered input-sm w-full mb-2"
              :disabled="isUploadingKb === true"
            />
            <textarea 
              v-model="kbTextInput"
              class="textarea textarea-bordered w-full" 
              rows="4" 
              placeholder="Paste or type text content here..."
              :disabled="isUploadingKb === true"
            ></textarea>
            <button 
              class="btn btn-sm w-full mt-2" 
              @click="uploadKnowledgeText" 
              :disabled="isUploadingKb === true || kbTextInput.trim().length === 0">
                <span v-if="isUploadingKb === true" class="loading loading-spinner loading-xs"></span>
                {{ isUploadingKb === true ? 'Adding Text...' : 'Add Text to Knowledge Base' }}
            </button>
            
          </div>
        </div>
      </div>


      <h4 class="font-semibold mb-2" :class="{ 'opacity-50': isUploadingKb === true }">Existing Files:</h4>
      <div class="max-h-80 overflow-y-auto space-y-2" :class="{ 'opacity-50 pointer-events-none': isUploadingKb === true }">
        <div v-if="isLoadingKb === true" class="text-center p-4">
          <span class="loading loading-dots loading-md"></span>
        </div>
        <div v-else-if="knowledgeBaseFiles.length === 0" class="text-center p-4 text-gray-500 italic">
          No knowledge base files found for this agent.
        </div>
        <div v-else v-for="file in knowledgeBaseFiles" :key="file.fileName" class="flex items-center justify-between bg-base-100 p-2 rounded shadow-sm border">
           <div class="flex items-center overflow-hidden mr-2">
             <Icon :icon="getFileIcon(file.fileType)" width="18" class="mr-2 flex-shrink-0"/> 
             <span class="text-sm truncate" :title="file.fileName">{{ file.fileType === 'txt' ? file.fileName.replace(/\.txt$/, '') : file.fileName }}</span>
             <span class="ml-2 text-xs text-gray-400">({{ file.fileType }})</span>
           </div>
           <button class="btn btn-xs btn-error btn-outline" @click="removeKnowledgeFile(file.fileName)" :disabled="isUploadingKb === true">
             <Icon icon="tabler:trash" width="14" style="color: #ef4444;" />
             Remove
           </button>
        </div>
      </div>
      
      <div class="modal-action mt-6">
        <button class="btn" @click="isKbModalOpen = false" :disabled="isUploadingKb === true">Close</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
       <button @click.prevent="handleBackdropClick">close</button>
    </form>
  </dialog>

  <InspectFile :is-open="isInspectModalOpen === true" :file="selectedFileToInspect" @close="closeInspectModal" />

  <dialog id="external_link_modal" class="modal" :class="{ 'modal-open': externalLinkModal.visible !== false }">
    <div class="modal-box" :style="{ 'direction': modalDir }">
    
      <h3 class="font-bold text-lg flex items-center space-x-2">
        <Icon icon="tabler:alert-circle" class="text-warning" width="24" />
        <span>{{ t('chatModals.leavingPlatform.title') }}</span>
      </h3>
      <p class="py-4">{{ t('chatModals.leavingPlatform.description') }}</p>
      <div class="py-2 px-1 text-sm bg-base-200 rounded-md overflow-x-auto whitespace-nowrap" dir="ltr">{{ externalLinkModal.url }}</div>
      <div class="modal-action" :class="{ 'justify-start': locale === 'he' }">
        <form method="dialog" class="flex space-x-2" :class="{ 'flex-row-reverse': locale === 'he' }">
          <a :href="externalLinkModal.url" target="_blank" rel="noopener noreferrer" @click="cancelExternalLink" class="btn">{{ t('chatModals.leavingPlatform.openButton') }}</a>
          <button @click="cancelExternalLink" class="btn">{{ t('chatModals.leavingPlatform.cancelButton') }}</button>
        </form>
      </div>
    </div>
  </dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, inject, onBeforeUnmount, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { Icon } from "@iconify/vue/dist/offline";
import { useToast } from 'vue-toastification';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { marked } from 'marked';
import * as XLSX from 'xlsx';
import InspectFile from '@/components/InspectFile.vue';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
import { useI18n } from '@/i18n';
import { v4 as uuidv4 } from 'uuid';
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';

const { t, currentLanguage } = useI18n();
const isRTL = computed(() => currentLanguage.value === 'he' || currentLanguage.value === 'ar');

const isEmbedded = computed(() => {
  try {
    return window.self !== window.top;
  } catch (error) {
    return true; 
  }
});

const getBrowserLanguage = () => {
  let lang = 'en';
  if (navigator.languages !== undefined && navigator.languages.length > 0) {
    lang = navigator.languages[0];
  } else if (isDefinedAndNotEmpty(navigator.language) === true) {
    lang = navigator.language;
  }
  return lang.split('-')[0];
};

const detectLanguageFromText = (text) => {
  if (typeof text !== 'string' || text.trim() === '') return null;
  if (/[\u0590-\u05FF]/.test(text) === true) return 'he';
  if (/[\u0600-\u06FF]/.test(text) === true) return 'ar';
  return null;
};

const getLocalizedMessage = (messageSet, language, fallback) => {
  if (typeof messageSet !== 'object' || messageSet === null) {
    return fallback;
  }
  
  if (messageSet['default'] !== undefined && messageSet['default'] !== null && messageSet['default'] !== '') {
    return messageSet['default'];
  }
  
  if (language !== undefined && language !== null && messageSet[language] !== undefined && messageSet[language] !== null && messageSet[language] !== '') {
    return messageSet[language];
  }
  
  if (messageSet.default !== undefined && messageSet.default !== null && messageSet.default !== '') {
    return messageSet.default;
  }
  
  if (isDefinedAndNotEmpty(messageSet['en']) === true) {
    return messageSet['en'];
  }
  return fallback;
};

const route = useRoute();
const router = useRouter();
const agentName = ref('Loading...');
const userMessage = ref('');
const messages = ref([]);
const chatMessages = ref(null);
const messageInput = ref(null);
const sessionId = ref(null);
const apiEndpoint = globalState.apiEndpoint;
const fileInput = ref(null);
const stagedFiles = ref([]);
const hideSidebar = inject('hideSidebar');
const isDragging = ref(false);
const inputDirection = ref('ltr'); 
const showAccessDeniedModal = ref(false);
const agentData = ref(null);
const toast = useToast();
const isLoadingAgentInfo = ref(true); 
const feedbackStatus = ref({});
const pendingFeedback = ref({});

const isKbModalOpen = ref(false);
const knowledgeBaseFiles = ref([]);
const kbStagedFiles = ref([]); 
const isUploadingKb = ref(false);
const isLoadingKb = ref(false);
const kbUploadError = ref(null);
const kbTextInput = ref('');
const kbTextTitle = ref('');
const sourceModal = ref({ visible: false, source: null });
const isInspectModalOpen = ref(false);
const selectedFileToInspect = ref(null);
const externalLinkModal = ref({ visible: false, url: null });

const userPermissions = computed(() => isDefinedAndNotEmpty(globalState.userInfo?.permissions) === true ? globalState.userInfo?.permissions : []);
const canModifyAgent = computed(() => {
  const hasPermission = userPermissions.value.includes('modifyAiAgent') === true || userPermissions.value.includes('fullAccessToOrganization') === true;
  return hasPermission;
});

const getAuthToken = () => {
  const token = document.cookie.split('; ').find(row => row.startsWith('authToken=') === true)?.split('=')[1];
  if (token === undefined || token === null) {
    return '';
  }
  return token;
};

const getAuthHeaders = (contentType = 'application/json') => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': contentType
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const handleAuthError = (error) => {
  if (error.response?.status === 401 || error.response?.status === 403) {
    router.push('/login');
  }
};

onMounted(async () => {
  hideSidebar.value = true;
  const agentId = route.params.agentid;
  try {
    await fetchAgentInfo(agentId);

    if (agentData.value === null) {
       throw new Error("Agent data could not be fetched.");
    }

    
    const startingMessages = agentData.value.startingMessage != null ? agentData.value.startingMessage : {};
    const browserLang = getBrowserLanguage();
    const rawStartingMessage = getLocalizedMessage(startingMessages, browserLang, "Hi, how can I help you today?");
    const parsedStartingHtml = sanitizeHtml(marked.parse(rawStartingMessage));
    const startingTextContent = htmlToText(parsedStartingHtml);
    const direction = getTextDirection(startingTextContent);
    appendMessage('agent', parsedStartingHtml, null, null, false, direction);

    await createNewSession(agentId);
  } catch (error) {
    console.error("Error during component mount:", error);
    if (error.response?.status === 403) {
        console.warn("Access denied fetching agent info.");
        
        agentName.value = (agentData.value !== undefined && agentData.value !== null && agentData.value.name !== undefined) ? agentData.value.name : 'Agent'; 
        showAccessDeniedModal.value = true;
    } else {
       handleAuthError(error); 
    }
  } finally {
    isLoadingAgentInfo.value = false; 
  }
});

onBeforeUnmount(() => {
  for (let i = 0; i < stagedFiles.value.length; i = i + 1) {
    if (stagedFiles.value[i].previewUrl !== null) {
      URL.revokeObjectURL(stagedFiles.value[i].previewUrl);
    }
  }
});

async function fetchAgentInfo(agentId) {
  try {
    const response = await axios.get(`${apiEndpoint}/getAIChatInfo/${agentId}`, {
      headers: getAuthHeaders()
    });
    console.log('Full response from /getAIChatInfo:', response);

    if (response === undefined || response === null || response.data === undefined || response.data === null) {
      console.error('Invalid or empty response from /getAIChatInfo');
      agentName.value = 'Error Fetching Name';
      agentData.value = null;
      throw new Error('Invalid response from server when fetching agent info.');
    }

    agentData.value = response.data;

    if (typeof response.data.name === 'string') {
      if (response.data.name.length > 0) {
        agentName.value = response.data.name;
      } else {
        agentName.value = 'Unnamed Agent';
      }
    } else if (response.data.name === null) {
      agentName.value = 'No Named Agent';
    } else {
      console.warn('Agent name is missing or not a string/null in response data:', response.data.name);
      agentName.value = 'Agent Name Error';
    }

  } catch (error) {
    console.error('Error fetching agent info:', error);
    agentName.value = 'Unknown Agent';
    agentData.value = null;
    if (error.response === undefined || error.response === null || error.response.status !== 403) {
        handleAuthError(error);
    }
    throw error;
  }
}


const enhanceTablesInMessage = (messageElement) => {
  if (messageElement === null) {
    return;
  }
  const tables = messageElement.querySelectorAll('table');
  
  for (let i = 0; i < tables.length; i = i + 1) {
    const table = tables[i];

    if (table.parentElement !== null && table.parentElement.classList.contains('markdown-table-wrapper') === true) {
        continue;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'markdown-table-wrapper';

    const button = document.createElement('button');
    button.className = 'download-table-btn';
    button.style.display = 'flex';
    button.style.flexDirection = 'row-reverse';
    const svgNs = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNs, 'svg');
    svg.setAttribute('xmlns', svgNs);
    svg.setAttribute('width', '12');
    svg.setAttribute('height', '12');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('class', 'mr-3');
    const path = document.createElementNS(svgNs, 'path');
    path.setAttribute('fill', 'currentColor');
    path.setAttribute('d', 'M5 20h14v-2H5m14-9h-4V3H9v6H5l7 7l7-7Z');
    svg.appendChild(path);
    button.appendChild(svg);
    button.appendChild(document.createTextNode(' XLSX'));
    button.title = 'Download table as XLSX';

    button.addEventListener('click', () => {
      downloadTableAsXlsx(table);
    });

    if (table.parentNode !== null) {
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(button);
        wrapper.appendChild(table);
    } else {
        console.warn('Table element did not have a parentNode, could not wrap.', table);
    }
  }
};

async function createNewSession(agentId) {
  try {
    const response = await axios.post(`${apiEndpoint}/createNewSession`, {
      agentId: agentId
    }, {
      headers: getAuthHeaders()
    });
    if (response.data !== null && response.data.sessionId !== null && response.data.sessionId.length > 0) {
      sessionId.value = response.data.sessionId;
    }
  } catch (error) {
    console.error('Error creating session:', error);
    handleAuthError(error);
    appendMessage('agent', 'Sorry, there was an error starting the chat. Please try again.', null, null, false, 'ltr');
  }
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const fileType = isDefinedAndNotEmpty(file.name.split('.').pop()?.toLowerCase()) === true ? file.name.split('.').pop()?.toLowerCase() : 'unknown';
      const isImage = file.type.startsWith('image/') === true;
      resolve({ dataUrl, fileType, isImage });
    };
    reader.onerror = error => reject(error);
    reader.readAsDataURL(file);
  });
}

function getTextDirection(text) {
  if (typeof text !== 'string' || text.length === 0) {
    return 'ltr';
  }
  const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF]/;
  return rtlRegex.test(text) ? 'rtl' : 'ltr';
}

function parseHtmlDocument(html) {
  const raw = typeof html === 'string' ? html : '';
  if (raw.length === 0) {
    return null;
  }
  if (typeof DOMParser === 'undefined') {
    return null;
  }
  try {
    return new DOMParser().parseFromString(raw, 'text/html');
  } catch (e) {
    return null;
  }
}

function htmlToText(html) {
  const doc = parseHtmlDocument(html);
  if (doc !== null && doc.body !== undefined && doc.body !== null) {
    const tc = doc.body.textContent !== undefined && doc.body.textContent !== null ? doc.body.textContent : (doc.body.innerText !== undefined && doc.body.innerText !== null ? doc.body.innerText : '');
    return typeof tc === 'string' ? tc : '';
  }
  const raw = typeof html === 'string' ? html : '';
  return raw.replace(/<[^>]*>/g, ' ');
}

function checkDirection(text) {
  inputDirection.value = getTextDirection(text);
}

function copyToClipboard(htmlContent) {
  if (isDefinedAndNotEmpty(htmlContent) === false) return;

  const textToCopy = htmlToText(htmlContent);

  if (textToCopy.trim().length === 0) {
    toast.warning('Nothing to copy.');
    return;
  }

  navigator.clipboard.writeText(textToCopy.trim()).then(() => {
    toast.success('Copied to clipboard!');
  }).catch(err => {
    console.error('Failed to copy text: ', err);
    toast.error('Failed to copy text.');
  });
}

async function sendMessage() {
  console.log('sendMessage function called, current sessionId:', sessionId.value);
  if (userMessage.value.trim().length <= 0) return;
  
  if (sessionId.value === null || sessionId.value === undefined || sessionId.value === '') {
    console.log('No session yet, creating new session');
    await createNewSession(route.params.agentid);
    console.log('New sessionId after createNewSession:', sessionId.value);
    
    if (sessionId.value === null || sessionId.value === undefined || sessionId.value === '') {
      appendMessage('agent', 'Session is not established. Please refresh the page or try again.', null, null, false, 'ltr');
      return;
    }
  }
  
  const messageText = userMessage.value;
  userMessage.value = ''; 
  checkDirection('');

  let filesPayload = [];
  let filesForDisplay = [];

  if (stagedFiles.value.length > 0) {
    const currentStagedFiles = [...stagedFiles.value];
    try {
      const fileReadPromises = currentStagedFiles.map(fileItem => 
        readFileAsBase64(fileItem.file)
      );
      const fileReadResults = await Promise.all(fileReadPromises);

      filesPayload = fileReadResults.map((result, index) => ({
        fileBase64Content: result.dataUrl.split(',')[1], 
        fileType: result.fileType
      }));

      filesForDisplay = fileReadResults.map((result, index) => ({
        name: currentStagedFiles[index].file.name,
        type: result.fileType,
        dataUrl: result.isImage === true ? result.dataUrl : null 
      }));

      for (let i = 0; i < currentStagedFiles.length; i++) {
        if (currentStagedFiles[i].previewUrl !== null) {
          URL.revokeObjectURL(currentStagedFiles[i].previewUrl);
        }
      }

      stagedFiles.value = [];

    } catch (error) {
      console.error("Error reading files:", error);
      appendMessage('agent', 'Error processing files. Please try again.', null, null, false, 'ltr');
      stagedFiles.value = [];
      return; 
    }
  }
  
  const userMsgDirection = getTextDirection(messageText);
  appendMessage('user', messageText, filesForDisplay, null, false, userMsgDirection); 
  
  const agentMessageId = `agent-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  appendMessage('agent', '', null, agentMessageId, true);
  
  await nextTick();
  scrollToBottom();

  try {
    const requestBody = {
      sessionId: sessionId.value,
      question: messageText,
      agentId: route.params.agentid
    };

    if (filesPayload.length > 0) {
      requestBody.files = filesPayload; 
    }

    const response = await axios.post(`${apiEndpoint}/query`, requestBody, {
      headers: getAuthHeaders()
    });
    
    if (response.data !== null && response.data.answer !== null) {
      console.log('[ChatAgentAssistance] Received response from /query:', response.data);
      updateAgentMessage(agentMessageId, response.data.answer, response.data.messageId, response.data.suggestedResponses, response.data.sources);
    } else {
      updateAgentMessage(agentMessageId, 'Sorry, I received an empty response.', undefined, [], []);
    }
  } catch (error) {
    console.error('Error sending message:', error);
    handleAuthError(error);
    updateAgentMessage(agentMessageId, 'Sorry, there was an error processing your message. Please try again.', undefined, [], []);
  }
}

function appendMessage(type, text, files = null, id = null, displayLoadingEffect = false, calculatedDirection = 'ltr') {
  const messageData = { 
    type, 
    text, 
    id, 
    isLoading: displayLoadingEffect === true, 
    attachedFiles: files,
    direction: displayLoadingEffect === true ? 'ltr' : calculatedDirection,
    suggestedResponses: [],
    sources: []
  };
  messages.value.push(messageData);
  nextTick(() => {
    scrollToBottom();
  });
}

function updateAgentMessage(id, newText, messageId, suggestedResponses = [], providedSources = null) {
  console.log(`[ChatAgentAssistance] updateAgentMessage called with messageId: ${messageId}`);
  const messageIndex = messages.value.findIndex(msg => msg.id === id && msg.type === 'agent');
  if (messageIndex > -1) {

    let cleanText = isDefinedAndNotEmpty(newText) === true ? newText : '';
    let sourcesToUse = Array.isArray(providedSources) === true && providedSources.length > 0 ? providedSources : null;

    if (sourcesToUse === null) {
      const parsedDataTmp = extractSources(cleanText);
      cleanText = parsedDataTmp.cleanText;
      sourcesToUse = parsedDataTmp.sources;
    }

    const parsedHtml = sanitizeHtml(marked.parse(cleanText));
    const direction = getTextDirection(htmlToText(parsedHtml));

    
    messages.value[messageIndex].text = parsedHtml; 
    messages.value[messageIndex].isLoading = false;
    messages.value[messageIndex].messageId = messageId;
    messages.value[messageIndex].suggestedResponses = isDefinedAndNotEmpty(suggestedResponses) === true ? suggestedResponses : [];
    messages.value[messageIndex].sources = getUniqueSources(sourcesToUse);
    console.log('[ChatAgentAssistance] Agent message object after ID assignment:', messages.value[messageIndex]);
    messages.value[messageIndex].direction = direction; 
    messages.value[messageIndex].domId = `message-content-${isDefinedAndNotEmpty(messageId) === true ? messageId : id}`;
    nextTick(() => {
      scrollToBottom();
 
      const updatedMessageElement = chatMessages.value?.querySelector(`.message[message-id="${id}"] .message-content`);
      enhanceTablesInMessage(updatedMessageElement);
    });
  }
}

function scrollToBottom() {
  if (chatMessages.value !== null) {
    chatMessages.value.scrollTop = chatMessages.value.scrollHeight;
  }
}

function triggerFileInput() {
  fileInput.value.click();
}

function handleDragEnter(event) {
  isDragging.value = true;
}

function handleDragOver(event) {

}

function handleDragLeave(event) {

  if (event.currentTarget.contains(event.relatedTarget) === true) return;
  isDragging.value = false;
}

function handleDrop(event) {
  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files === undefined || files.length <= 0) return;
  processFiles(files); 
}

function processFiles(files) {
  const newFiles = Array.from(files)
    .filter(file => file.type !== "") 
    .map(file => {
      let previewUrl = null;
      if (file.type.startsWith('image/') === true) {
        previewUrl = URL.createObjectURL(file);
      }
      return { file, previewUrl };
    });


  const uniqueNewFiles = newFiles.filter(newFile => 
    stagedFiles.value.some(existingFile => 
      existingFile.file.name === newFile.file.name && existingFile.file.size === newFile.file.size
    ) === false
  );

  stagedFiles.value = [...stagedFiles.value, ...uniqueNewFiles];
}

function handleFileUpload(event) {
  const files = event.target.files;
  if (files === null || files.length <= 0) return;
  
  processFiles(files); 

  event.target.value = ''; 
}

function removeStagedFile(index) {
  const fileItem = stagedFiles.value[index];
  if (fileItem !== undefined && fileItem.previewUrl !== null) {
    URL.revokeObjectURL(fileItem.previewUrl);
  }
  stagedFiles.value.splice(index, 1);
}

function getFileIcon(fileType) {
  const extension = fileType?.toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(extension) === true) {
    return null;
  }
  switch (extension) {
    case 'pdf': return 'tabler:file-type-pdf';
    case 'doc':
    case 'docx': return 'tabler:file-type-docx';
    case 'txt': return 'tabler:file-type-txt';
    case 'csv': return 'tabler:file-type-csv';
    case 'json': return 'tabler:file-type-json';
    case 'md': return 'tabler:file-type-md';
    default: return 'tabler:file';
  }
}

function startNewChat() {
  router.go(0);
}

function openKbModal() {
  fetchKnowledgeBase();
  kbStagedFiles.value = [];
  isKbModalOpen.value = true;
}

async function fetchKnowledgeBase() {
  if (isLoadingKb.value === true) return;
  isLoadingKb.value = true;
  knowledgeBaseFiles.value = [];
  const agentId = route.params.agentid;
  try {
    const response = await axios.get(`${apiEndpoint}/getAgentKnowledgebase/${agentId}`, {
      headers: getAuthHeaders()
    });
    knowledgeBaseFiles.value = isDefinedAndNotEmpty(response.data) === true ? response.data : [];
  } catch (error) {
    console.error("Error fetching knowledge base:", error);
    toast.error("Failed to load knowledge base files.");
    handleAuthError(error);
  } finally {
    isLoadingKb.value = false;
  }
}

function handleKbFileUpload(event) {
  const files = event.target.files;
  if (files === null || files.length <= 0) return;
  
  const newFiles = Array.from(files)
    .filter(file => file.type !== "")
    .map(file => ({ file, previewUrl: null }));

  const uniqueNewFiles = newFiles.filter(newFile => 
    !kbStagedFiles.value.some(existingFile => 
        existingFile.file.name === newFile.file.name && existingFile.file.size === newFile.file.size
    )
  );
  kbStagedFiles.value = [...kbStagedFiles.value, ...uniqueNewFiles];
  event.target.value = '';
}

function removeKbStagedFile(index) {
  kbStagedFiles.value.splice(index, 1);
}

async function uploadKnowledgeFiles() {
  if (kbStagedFiles.value.length === 0 || isUploadingKb.value === true) return;
  kbUploadError.value = null;
  isUploadingKb.value = true;
  const agentId = route.params.agentid;

  let filesPayload = [];
  try {
    const fileReadPromises = kbStagedFiles.value.map(item => readFileAsBase64(item.file));
    const fileReadResults = await Promise.all(fileReadPromises);
    
    filesPayload = fileReadResults.map((result, index) => ({
        fileName: kbStagedFiles.value[index].file.name,
        fileType: result.fileType,
        fileData: result.dataUrl.split(',')[1],
        status: 'new'
      }));

  } catch (error) {
      console.error("Error reading files for KB upload:", error);
      toast.error("Error preparing files for upload.");
      isUploadingKb.value = false;
      return;
  }

  try {
    const response = await axios.put(`${apiEndpoint}/addAgentKnowledgebase/${agentId}`, 
      { files: filesPayload }, 
      { headers: getAuthHeaders() } 
    );
    toast.success(isDefinedAndNotEmpty(response.data.message) === true ? response.data.message : "Files added successfully!");
    kbStagedFiles.value = [];
    await fetchKnowledgeBase();
  } catch (error) {
    console.error("Error uploading knowledge base files:", error);
    let errorMessage = "Failed to upload files.";
    if (error.response?.status === 400) {
        errorMessage = error.response?.data?.error ? 
            `Upload failed: ${error.response.data.error}. Please check files and try again.` :
            "Upload failed due to invalid request. Please check files and try again.";
    } else if (error.response?.data?.error !== undefined) {
       errorMessage = error.response.data.error;
    } else {
       errorMessage = "An unexpected error occurred during upload.";
    }
    
    kbUploadError.value = errorMessage;

    handleAuthError(error);
  } finally {
    isUploadingKb.value = false;
  }
}

async function removeKnowledgeFile(fileName) {
  if (confirm(`Are you sure you want to remove the file "${fileName}"?`) === false) {
     return;
  }
  const agentId = route.params.agentid;
  try {
    const response = await axios.delete(`${apiEndpoint}/removeAgentKnowledgebase/${agentId}?fileName=${encodeURIComponent(fileName)}`, {
      headers: getAuthHeaders()
    });
    toast.success(isDefinedAndNotEmpty(response.data.message) === true ? response.data.message : 'File removed successfully!');
    await fetchKnowledgeBase();
  } catch (error) {
    console.error("Error removing knowledge base file:", error);
     const errorMessage = isDefinedAndNotEmpty(error.response?.data?.error) === true ? error.response?.data?.error : "Failed to remove file.";
    toast.error(errorMessage);
    handleAuthError(error);
  }
}

function handleBackdropClick() {
  if (isUploadingKb.value === false) {
    isKbModalOpen.value = false;
  }
}

async function uploadKnowledgeText() {
  if (kbTextInput.value.trim().length === 0 || isUploadingKb.value === true) return;

  kbUploadError.value = null;
  isUploadingKb.value = true;
  const agentId = route.params.agentid;

  let textPayload = [];
  try {
    const textContent = kbTextInput.value.trim();
    const title = kbTextTitle.value.trim();
    const fileName = title.length > 0 
      ? `${title}.txt` 
      : `text-snippet-${Date.now()}.txt`;
    
    const fileData = btoa(unescape(encodeURIComponent(textContent)));

    textPayload = [{
      fileName: fileName,
      fileType: 'txt',
      fileData: fileData,
      status: 'new'
    }];

  } catch (error) {
      console.error("Error encoding text for KB upload:", error);
      toast.error("Error preparing text for upload.");
      kbUploadError.value = "Error encoding text.";
      isUploadingKb.value = false;
      return;
  }

  try {
    const response = await axios.put(`${apiEndpoint}/addAgentKnowledgebase/${agentId}`, 
      { files: textPayload }, 
      { headers: getAuthHeaders() } 
    );
    toast.success(isDefinedAndNotEmpty(response.data.message) === true ? response.data.message : "Text snippet added successfully!");
    kbTextInput.value = ''; 
    kbTextTitle.value = '';
    await fetchKnowledgeBase(); 
  } catch (error) {
    console.error("Error uploading text snippet:", error);
    let errorMessage = "Failed to upload text snippet.";
    if (error.response?.status === 400) {
        errorMessage = error.response?.data?.error ? 
            `Upload failed: ${error.response.data.error}.` :
            "Upload failed due to invalid request.";
    } else if (error.response?.data?.error !== undefined) {
       errorMessage = error.response.data.error;
    } else {
       errorMessage = "An unexpected error occurred during upload.";
    }
    
    kbUploadError.value = errorMessage;
    handleAuthError(error);
  } finally {
    isUploadingKb.value = false;
  }
}

async function submitFeedback(messageId, feedbackType) {
  if (messageId === undefined) {
    console.error('Cannot submit feedback: messageId is missing.');
    return;
  }
  if (pendingFeedback.value[messageId] === true) {
    console.log(`Feedback submission already pending for ${messageId}`);
    return; 
  }

  console.log(`Submitting feedback: ${feedbackType} for message ${messageId}`);
  pendingFeedback.value[messageId] = true;
  
  try {
    await axios.post(`${apiEndpoint}/api/feedback`, 
      { messageId, feedbackType }, 
      { headers: getAuthHeaders() }
    );
    feedbackStatus.value[messageId] = feedbackType;
    toast.success('Feedback submitted!');

  } catch (error) {
    console.error('Error submitting feedback:', error);
    toast.error('Failed to submit feedback.');
    handleAuthError(error);
  } finally {
    pendingFeedback.value[messageId] = false;
  }
}


const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};


async function downloadTxt(message) {
  console.log('Download TXT for message:', message.messageId);
  try {
    const textContent = htmlToText(message.text);

    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    triggerDownload(blob, `agent_response_${isDefinedAndNotEmpty(message.messageId) !== false ? message.messageId : Date.now()}.txt`);
    toast.success('TXT file download started.');
  } catch (error) {
    console.error('Error generating TXT:', error);
    toast.error('Failed to generate TXT file.');
  }
}

async function downloadDocx(message) {
  console.log('Download DOCX for message:', message.messageId);
  try {
    const docTmp = parseHtmlDocument(message.text);
    const paragraphs = [];
    const nodes = docTmp !== null && docTmp.body !== undefined && docTmp.body !== null ? Array.from(docTmp.body.childNodes) : [];

    nodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        paragraphs.push(new Paragraph({ children: [new TextRun(node.textContent)] }));
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.tagName === 'P') {
           paragraphs.push(new Paragraph({ children: [new TextRun(node.textContent)] }));
        } else if (node.tagName === 'BR') {
           paragraphs.push(new Paragraph("")); 
        } else {
     
          paragraphs.push(new Paragraph({ children: [new TextRun(node.textContent)] }));
        }
     
      }
    });

    if (paragraphs.length === 0) {
      const tc = htmlToText(message.text);
      if (tc.trim().length > 0) {
        paragraphs.push(new Paragraph({ children: [new TextRun(tc)] }));
      } else {
        paragraphs.push(new Paragraph({ children: [new TextRun('')] }));
      }
    }

    const doc = new Document({
      sections: [{
        properties: {},
        children: paragraphs,
      }],
    });

    const blob = await Packer.toBlob(doc);
    triggerDownload(blob, `agent_response_${isDefinedAndNotEmpty(message.messageId) !== false ? message.messageId : Date.now()}.docx`);
    toast.success('DOCX file download started.');

  } catch (error) {
    console.error('Error generating DOCX:', error);
    toast.error('Failed to generate DOCX file.');
  }
}

async function downloadPdf(message) {
  console.log('Download PDF for message:', message.messageId);
  
  const hiddenDiv = document.createElement('div');
  hiddenDiv.style.position = 'absolute';
  hiddenDiv.style.left = '-9999px';
  hiddenDiv.style.top = '0';
  hiddenDiv.style.width = '750px'; 
  hiddenDiv.style.padding = '25px'; 
  hiddenDiv.style.background = 'white';
  

  const textContent = htmlToText(message.text);

  const preElement = document.createElement('pre');
  preElement.style.whiteSpace = 'pre-wrap';      
  preElement.style.wordWrap = 'break-word';      
  preElement.style.margin = '0';                   
  preElement.style.fontFamily = 'sans-serif';      
  preElement.style.fontSize = '14px';            
  preElement.style.color = '#1f2937';             
  preElement.textContent = textContent;
  

  preElement.dir = isDefinedAndNotEmpty(message.direction) !== false ? message.direction : 'ltr'; 
  preElement.style.textAlign = message.direction === 'rtl' ? 'right' : 'left';

  hiddenDiv.appendChild(preElement);
  
  document.body.appendChild(hiddenDiv);

  try {

     await new Promise(resolve => setTimeout(resolve, 50)); 


    const canvas = await html2canvas(hiddenDiv, {
      scale: 2, 
      useCORS: true, 
      logging: false, 
      windowWidth: hiddenDiv.scrollWidth,
      windowHeight: hiddenDiv.scrollHeight 
    });
    
    document.body.removeChild(hiddenDiv);

    const imgData = canvas.toDataURL('image/png');
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'pt', 
      format: 'a4' 
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfPageHeight = pdf.internal.pageSize.getHeight(); 

    const ratio = pdfWidth / imgWidth;
    const scaledCanvasHeight = imgHeight * ratio; 
    
    const numPages = Math.ceil(scaledCanvasHeight / pdfPageHeight);

    let yPositionOnImage = 0; 

    for (let i = 0; i < numPages; i = i + 1) {
      if (i > 0) {
        pdf.addPage();
      }
      
      const yPositionOnPdf = -i * pdfPageHeight; 
      
      pdf.addImage(imgData, 'PNG', 0, yPositionOnPdf, pdfWidth, scaledCanvasHeight);
      
    }

    pdf.save(`agent_response_${isDefinedAndNotEmpty(message.messageId) !== false ? message.messageId : Date.now()}.pdf`);
    toast.success('PDF file download started.');

  } catch (error) {
    console.error('Error generating PDF:', error);
    toast.error('Failed to generate PDF file.');
    if (document.body.contains(hiddenDiv)) {
        document.body.removeChild(hiddenDiv);
    }
  }
}



function downloadTableAsXlsx(tableElement) {
  if (tableElement === null || tableElement.tagName !== 'TABLE') {
    console.error('Invalid element provided for XLSX download:', tableElement);
    toast.error('Could not find table data to download.');
    return;
  }

  try {

    const ws = XLSX.utils.table_to_sheet(tableElement);
    
 
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');


    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });


    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    triggerDownload(blob, `table_export_${Date.now()}.xlsx`);
    toast.success('XLSX file download started.');

  } catch (error) {
    console.error('Error generating XLSX:', error);
    toast.error('Failed to generate XLSX file.');
  }
}

async function handleSuggestionClick(suggestionText, messageId) {
  const message = messages.value.find(m => m.messageId === messageId);
  if (message !== undefined && message !== null) {
    message.suggestedResponses = [];
  }
  
  appendMessage('user', suggestionText);
  
  const agentMessageId = `agent-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  appendMessage('agent', '', null, agentMessageId, true);
  
  await nextTick();
  scrollToBottom();

  try {
    const requestBody = {
      sessionId: sessionId.value,
      question: suggestionText,
      agentId: route.params.agentid
    };

    const response = await axios.post(`${apiEndpoint}/query`, requestBody, {
      headers: getAuthHeaders()
    });
    
    if (response.data !== null && response.data.answer !== null) {
      updateAgentMessage(agentMessageId, response.data.answer, response.data.messageId, response.data.suggestedResponses, response.data.sources);
    } else {
      updateAgentMessage(agentMessageId, 'Sorry, I received an empty response.', undefined, [], []);
    }
  } catch (error) {
    console.error('Error sending message from suggestion:', error);
    handleAuthError(error);
    updateAgentMessage(agentMessageId, 'Sorry, there was an error processing your message. Please try again.', undefined, [], []);
  }
}

function extractSources(text) {
  const sources = [];
  const regex = /Source\s+\d+\s*:\s*(.*?)\nHighlight:\s*(.*?)(?:\n|$)/gs;
  let cleaned = text;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const detail = match[1].trim();
    const highlight = match[2].trim();
    if (detail.length > 0) {
      let sourceObj;
      if (detail.startsWith('http') === true) {
        sourceObj = { type: 'website', url: detail, highlight };
      } else {
        const pageMatch = detail.match(/page\s+(\d+)/i);
        const page = pageMatch !== null ? parseInt(pageMatch[1]) : null;
        const name = detail.replace(/page\s+\d+/i, '').trim();
        sourceObj = { type: 'file', name, page, highlight };
      }
      sources.push(sourceObj);
    }
    cleaned = cleaned.replace(match[0], '');
  }
  cleaned = cleaned.trim();
  return { cleanText: cleaned, sources };
}

function sanitizeWindowOpenUrl(value) {
  if (value === undefined || value === null) {
    return null;
  }
  const raw = String(value).trim();
  if (raw.length === 0) {
    return null;
  }
  let urlObj;
  try {
    urlObj = new URL(raw, window.location.origin);
  } catch (e) {
    return null;
  }
  const protocol = typeof urlObj.protocol === 'string' ? urlObj.protocol.toLowerCase() : '';
  const isProd = process.env.environment === 'production';
  if (protocol === 'https:') {
    return urlObj.href;
  }
  if (protocol === 'http:' && isProd === false) {
    return urlObj.href;
  }
  return null;
}

function openSourceModal(src) {
  if (src.type === 'website') {
    const safe = sanitizeWindowOpenUrl(src.url);
    if (safe !== null) {
      window.open(safe, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Invalid URL.');
    }
    return;
  }
  openInspectModal(src);
}

function openInspectModal(src) {
  const agentId = route.params.agentid;
  if (agentId === undefined || agentId === null) {
    toast.error('Agent ID missing.');
    return;
  }
  const fileObj = {
    fileName: src.name,
    fileType: isDefinedAndNotEmpty(src.name.split('.').pop()?.toLowerCase()) === true ? src.name.split('.').pop()?.toLowerCase() : 'file',
    highlight: src.highlight,
    page: src.page,
    isLoading: true
  };
  selectedFileToInspect.value = { ...fileObj };
  isInspectModalOpen.value = true;
  axios.get(`${apiEndpoint}/api/agents/${agentId}/files/${encodeURIComponent(src.name)}`, {
    headers: getAuthHeaders(),
    responseType: 'blob'
  }).then(response => {
    const reader = new FileReader();
    reader.onload = e => {
      const base64Data = e.target.result.split(',')[1];
      let textContent = null;
      if (['txt', 'text', 'md', 'json', 'csv'].includes(fileObj.fileType) === true) {
        try {
          textContent = atob(base64Data);
        } catch (error) {
          textContent = null;
        }
      }
      selectedFileToInspect.value = { ...fileObj, fileData: base64Data, content: textContent, isLoading: false };
    };
    reader.readAsDataURL(response.data);
  }).catch(err => {
    toast.error('Failed to load file.');
    selectedFileToInspect.value = { ...fileObj, error: 'Failed to load content.', isLoading: false };
  });
}

function closeInspectModal() {
  isInspectModalOpen.value = false;
  selectedFileToInspect.value = null;
}

function getFileIconByType(type){
  const t = (isDefinedAndNotEmpty(type) === true ? type : '').toLowerCase();
  switch(t){
    case 'pdf': return 'tabler:file-type-pdf';
    case 'doc': case 'docx': return 'tabler:file-type-docx';
    case 'xlsx': case 'xls': return 'tabler:file-type-xlsx';
    case 'csv': return 'tabler:file-type-csv';
    case 'txt': case 'text': return 'tabler:file-type-txt';
    case 'md': return 'tabler:file-type-md';
    default: return 'tabler:file';
  }
}

function confirmExternalLink(src) {
  externalLinkModal.value = { visible: true, url: src.url };
}

function openExternalLink() {
  if (externalLinkModal.value.url !== null) {
    const safe = sanitizeWindowOpenUrl(externalLinkModal.value.url);
    if (safe !== null) {
      window.open(safe, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Invalid URL.');
    }
  }
  externalLinkModal.value.visible = false;
}

function cancelExternalLink() {
  externalLinkModal.value.visible = false;
}

const getUniqueSources = (sources) => {
  if (Array.isArray(sources) === false || sources.length === 0) {
    return [];
  }
  const uniqueSources = [];
  const seen = new Set();

  for (let i = 0; i < sources.length; i = i + 1) {
    const source = sources[i];
    let key;
    if (source.type === 'website') {
      key = 'website|' + source.url;
    } else if (source.type === 'file') {
      key = 'file|' + source.name + '|' + source.page;
    } else {
      key = JSON.stringify(source);
    }
    if (seen.has(key) === false) {
      uniqueSources.push(source);
      seen.add(key);
    }
  }
  return uniqueSources;
};

const modalTitle = computed(() => t('chatModals.leavingPlatform.title'));
const modalDir = computed(() => getTextDirection(modalTitle.value));
console.log(modalTitle.value, modalDir.value);
</script>

<style scoped>
#footer {    display: none;}

#knowledge-base-fab {
  display: none;
}

@media (min-width: 768px) {
  #knowledge-base-fab {
    display: inline-flex; 
    align-items: center;
    justify-content: center;
  }
}

.chat-assistance-container {
  height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  margin-left: 0;
  width: 100%;
  
}

.chat-assistance-container.rtl {
  direction: rtl !important;
}

.chat-header {
  padding: 1.5rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  direction: ltr !important;
}

.chat-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
}

.agent-info {
  display: flex;
  align-items: center;
  
}
.agent-info > * + * { margin-left: 1rem; }

.agent-name {
  font-weight: 700;
  color: #1e293b;
  font-size: 1.25rem;
}

.agent-status {
  padding: 0.25rem 0.75rem;
  background: #dcfce7;
  color: #166534;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
}
[id='footer']{background:#FFF}
.chat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;
  position: relative;
  width: 100%;
  direction: ltr !important;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
  padding-bottom: 10rem;
  display: flex;
  flex-direction: column;
  
  max-height: calc(100% - 8rem);
  overflow-x: hidden;
  direction: ltr !important;
}
.chat-messages > * + * { margin-top: 1rem; }

.message {
  max-width: 80%;
  padding: 0.75rem 1rem;
  border-radius: 1rem;
  line-height: 1.5;
  opacity: 0;
  transform: translateY(20px);
  animation: messageAppear 0.3s ease forwards;
}

@keyframes messageAppear {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-message {
  align-self: flex-end;
  background: #2563eb;
  color: white;
  border-bottom-right-radius: 0.25rem;
  animation: userMessageAppear 0.3s ease forwards;
}

@keyframes userMessageAppear {
  to {
    opacity: 1;
    transform: translateX(0);
  }
  from {
    opacity: 0;
    transform: translateX(20px);
  }
}
.message-wrapper.message-content div {height: fit-content !important; }
.agent-message {
  align-self: flex-start;
  background: #f1f5f9;
  color: #1e293b;
  border-bottom-left-radius: 0.25rem;
  animation: agentMessageAppear 0.3s ease forwards;
}

@keyframes agentMessageAppear {
  to {
    opacity: 1;
    transform: translateX(0);
  }
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
}


.chat-input {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 2rem);
  max-width: 800px;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 0 6px -1px rgb(0 0 0 / 29%), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 10;
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  border: 2px dashed transparent;
  transition: border-color 0.2s ease-in-out, background-color 0.2s ease-in-out;
}
.is-dragging-over {
  border-color: #2563eb;
  background-color: #eff6ff;
}

.staged-files-container {
  display: flex;
  flex-wrap: wrap;
  margin-top: -0.5rem;
  margin-left: -0.5rem;
  padding: 0.2rem 0.5rem 0.6rem;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 0.5rem;
}
.staged-files-container > * { margin-top: 0.5rem; margin-left: 0.5rem; }

.file-card {
  display: flex;
  align-items: center;
  background-color: #eef2ff;
  color: #4338ca;
  padding: 0.25rem 0.5rem;
  border-radius: 0.75rem;
  font-size: 0.8rem;
  max-width: 180px;
  border: 1px solid #c7d2fe;
  overflow: hidden;
}

.file-preview-image {
  width: 24px;
  height: 24px;
  object-fit: cover;
  margin-right: 0.4rem;
  border-radius: 4px;
  flex-shrink: 0;
}

.file-icon {
  margin-right: 0.3rem;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

.file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-grow: 1;
}

.remove-file-button {
  background: none;
  border: none;
  color: #6366f1;
  cursor: pointer;
  padding: 0 0 0 0.3rem;
  margin-left: 0.2rem;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.remove-file-button:hover {
  color: #4338ca;
}

.input-row {
  display: flex;
  align-items: flex-start;
}
.input-row > * + * { margin-left: 0.5rem; }

.file-upload {
  align-items: center;
}

.upload-button {
  padding: 0.65rem;
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  height: 4.2rem;
}

.upload-button:hover {
  background: #e2e8f0;
  color: #475569;
}

.hidden {
  display: none;
}

textarea {
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  resize: none;
  min-height: 42px;
  max-height: 120px;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.5;
}
.chat-assistance-container.rtl textarea {
  direction: rtl !important;
  text-align: right !important;
}

textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.send-button {
  padding: 0.65rem;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  height: 42px;
}

.send-button:hover {
  background: #1d4ed8;
}

.send-button:active {
  background: #1e40af;
}

.message-content {
  white-space: wrap;
  word-wrap: break-word;
}

.message-content :deep(p) {
  
  line-height: 1.5;
  padding: 0;
}

.message-content :deep(ul),
.message-content :deep(ol) {
  margin: 0 0 0.5em 1.2em;
  padding: 0 0 0 1.2em;
}

.message-content :deep(h1),
.message-content :deep(h2),
.message-content :deep(h3),
.message-content :deep(h4),
.message-content :deep(h5),
.message-content :deep(h6) {
  margin: 0 0 0.5em 0;
  padding: 0;
}

.message-content :deep(table) {
  margin: 0.5em 0;
}

.message-content :deep(.markdown-table-wrapper) {
  position: relative;
  margin-top: 1em;
  margin-bottom: 1em;
  overflow-x: auto; 
  padding-top: 0.5em;
}

.message-content :deep(.download-table-btn) {
  position: absolute;
  top: 0px; 
  left: 5px;
  z-index: 5;
  background-color: #e2e8f0; 
  color: #4b5563; 
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 2px 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  opacity: 0.6; 
   
  width: 80px;
}

.message-content :deep(.markdown-table-wrapper:hover .download-table-btn) {
  opacity: 1; 
}

.message-content :deep(.download-table-btn:hover) {
  background-color: #cbd5e1; 
  color: #1f2937;
}

.message-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1.5em; 
  border-radius: 8px; 
  overflow: hidden; 
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.message-content :deep(th),
.message-content :deep(td) {
  border: 1px solid #e2e8f0; 
  padding: 8px 12px;
  text-align: left;
  
}

.message-content :deep(th) {
  background-color: #f1f5f9; 
  font-weight: 600;
}

.message-content :deep(tr:hover) {
  background-color: #f8fafc; 
}

.source-chip {
  background-color: #f1f5f9;
  padding: 0.25rem 0.5rem;
  border-radius: 0.5rem;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.2s, box-shadow 0.2s;
  display: inline-flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  max-width: 180px;
  overflow: hidden;
}
.source-chip > * + * { margin-left: 0.25rem; }

.source-chip:hover {
  background-color: #e2e8f0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}

.modal .modal-box p {
  direction: ltr;
}

.chat-assistance-container.embedded-view {
  height: 100vh;
  background: transparent;
}

.message-content :deep(div) {
  height: fit-content;
}

.feedback-buttons button {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #475569;
  border-radius: 4px;
  transition: background-color 0.15s ease, transform 0.1s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feedback-buttons button:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.1);
  color: #1e293b;
  transform: scale(1.1);
}

.feedback-buttons button:active:not(:disabled) {
  transform: scale(0.95);
}

.feedback-buttons button:disabled {
  cursor: default;
  opacity: 0.7;
}

.export-button-container {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  z-index: 1;
}

.export-button-container .btn {
  transition: background-color 0.15s ease, transform 0.1s ease;
}

.export-button-container .btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
  transform: scale(1.1);
}

.export-button-container .btn:active {
  transform: scale(0.95);
}

.sources-container {
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
}

.message-content :deep(ul),
.message-content :deep(ol) {
  margin: 0 0 0.5em 1.2em;
  padding: 0 0 0 1.2em;
}

.message-content :deep(p) {
  margin: 0 0 0.5em 0;
  line-height: 1.5;
  padding: 0;
}
</style> 